<script setup lang="ts">
import type { Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElIcon } from 'element-plus'

export interface NavItem {
  /** Route name to link to. */
  name: string
  /** Key in the locale file, e.g. 'nav.items.users'. */
  labelKey: string
  /** Icon component from @element-plus/icons-vue. */
  icon: Component
  /** Permission the backend requires for the screen this links to. */
  permission: string
}

defineProps<{
  title: string
  items: readonly NavItem[]
}>()

const { t } = useI18n()
</script>

<template>
  <div>
    <p class="label-caps px-3 pb-2">
      {{ title }}
    </p>

    <ul class="flex flex-col gap-1">
      <li v-for="item in items" :key="item.name">
        <RouterLink
          :to="{ name: item.name }"
          class="nav-link touch-target label-lg group relative flex min-h-10 items-center gap-3 rounded-lg px-3 text-ink-secondary transition-colors duration-150 hover:bg-ink/5 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
          active-class="nav-link-active bg-brand-500/10 font-semibold !text-brand-500 hover:bg-brand-500/15"
          aria-current-value="page"
        >
          <!-- Left accent bar, visible on the active item only. -->
          <span
            class="nav-accent absolute top-2 bottom-2 -left-0 w-[3px] rounded-full bg-brand-500 opacity-0 transition-opacity duration-150"
            aria-hidden="true"
          />
          <ElIcon :size="18" aria-hidden="true">
            <component :is="item.icon" />
          </ElIcon>
          <span class="truncate">{{ t(item.labelKey) }}</span>
        </RouterLink>
      </li>
    </ul>
  </div>
</template>

<style scoped>
.nav-link-active .nav-accent {
  opacity: 1;
}
</style>
